import { useState } from "react";
import { Package, Check, RefreshCcw, Star, XCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { PRODUCTS } from "../../data/products";
import { inputCls } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOOKUP_TO_PRODUCT = { morning_focus: "morning-focus", deep_flow: "deep-flow", evening_calming: "evening-calming", matcha_zen: "matcha-zen", duo_rituel: "duo-rituel", samples: "samples", cafe_morning_ritual: "cafe-morning-ritual", cafe_allday_peace: "cafe-allday-peace", morning_focus_sub: "morning-focus", deep_flow_sub: "deep-flow", evening_calming_sub: "evening-calming", matcha_zen_sub: "matcha-zen", duo_rituel_sub: "duo-rituel" };

export const StatusTimeline = ({ status, t }) => {
  const steps = ["confirmed", "preparing", "shipped", "delivered"];
  if (status === "cancelled" || status === "refunded") {
    return (
      <p className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wide text-[#C85A32]" data-testid="order-status-terminal">
        <XCircle size={13} /> {t.account.statuses[status]}
      </p>
    );
  }
  const idx = steps.indexOf(status);
  return (
    <div className="flex items-center gap-1 mt-4">
      {steps.map((s, i) => (
        <div key={s} className="flex-1 flex flex-col items-center gap-1.5">
          <div className="w-full flex items-center">
            {i > 0 && <div className={`h-px flex-1 ${i <= idx ? "bg-[#4A624E]" : "bg-stone-200"}`} />}
            <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${i <= idx ? "bg-[#4A624E] text-white" : "bg-stone-200 text-stone-400"}`}>
              {i <= idx ? <Check size={11} /> : <span className="w-1.5 h-1.5 rounded-full bg-current" />}
            </div>
            {i < steps.length - 1 && <div className={`h-px flex-1 ${i < idx ? "bg-[#4A624E]" : "bg-stone-200"}`} />}
          </div>
          <span className={`text-[9px] font-mono uppercase tracking-wide ${i <= idx ? "text-[#4A624E]" : "text-stone-400"}`}>{t.account.statuses[s]}</span>
        </div>
      ))}
    </div>
  );
};

const ReviewForm = ({ order, myReviews, lang, ta, onDone }) => {
  const products = [...new Set(order.items.map((it) => it.product_id || LOOKUP_TO_PRODUCT[it.lookup_key]).filter(Boolean))];
  const [pid, setPid] = useState(products[0]);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const existing = myReviews.find((r) => r.order_id === order.id && r.product_id === pid);
  const product = PRODUCTS.find((p) => p.id === pid);
  if (!products.length) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/my/reviews`, { order_id: order.id, product_id: pid, rating, text }, { withCredentials: true });
      toast.success(ta.reviewThanks);
      setText("");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-xl bg-[#FAF7F2] border border-stone-200 p-5" data-testid={`review-form-${order.order_number}`}>
      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-500 mb-3">{ta.reviewFor}</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {products.map((p) => (
          <button key={p} type="button" data-testid={`review-product-${p}`} onClick={() => setPid(p)}
            className={`rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wide border transition-colors ${pid === p ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600"}`}>
            {PRODUCTS.find((x) => x.id === p)?.name[lang] || p}
          </button>
        ))}
      </div>
      {existing ? (
        <p className="text-sm text-stone-600" data-testid="review-existing">
          <Check size={13} className="inline text-[#4A624E] mr-1" /> {ta.reviewDone} · {existing.approved ? ta.reviewPublished : ta.reviewPending}
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div className="flex gap-1" data-testid="review-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => setRating(n)} aria-label={`${n}/5`}>
                <Star size={20} className={n <= rating ? "fill-[#C85A32] text-[#C85A32]" : "text-stone-300"} />
              </button>
            ))}
          </div>
          <textarea data-testid="review-text" required minLength={10} maxLength={800} rows={3} className={inputCls} placeholder={`${product?.name[lang] || ""} — ${ta.reviewPlaceholder}`} value={text} onChange={(e) => setText(e.target.value)} />
          <button data-testid="review-submit" type="submit" disabled={busy} className="rounded-full bg-stone-900 text-white px-6 py-2.5 text-[11px] uppercase tracking-[0.15em] hover:bg-[#C85A32] transition-colors disabled:opacity-60">
            {busy ? "..." : ta.reviewSend}
          </button>
        </form>
      )}
    </div>
  );
};

export const OrderCard = ({ o, t, lang, myReviews, reload }) => {
  const ta = t.account;
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid={`account-order-${o.order_number}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm text-[#C85A32]">{o.order_number}</span>
          {o.type === "subscription" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#4A624E]/10 text-[#4A624E] px-3 py-1 text-[10px] font-mono uppercase tracking-wide">
              <RefreshCcw size={10} /> {ta.subscription}
            </span>
          )}
        </div>
        <span className="text-xs text-stone-500 font-mono">{new Date(o.created_at).toLocaleDateString(lang === "fr" ? "fr-CH" : "en-GB")}</span>
      </div>
      <ul className="mt-3 text-sm text-stone-600">
        {o.items.map((it) => <li key={it.product_id || it.lookup_key || it.name}>{it.name} × {it.quantity}</li>)}
      </ul>
      <div className="mt-2 flex items-center justify-between">
        <span className="font-mono text-sm text-stone-900">{o.total.toFixed(2)} CHF</span>
        {o.tracking_number && (
          <span className="text-xs font-mono text-stone-500">{ta.tracking}: <span className="text-stone-800">{o.tracking_number}</span>
            {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" data-testid={`track-link-${o.order_number}`} className="ml-2 text-[#C85A32] underline">{ta.track}</a>}
          </span>
        )}
      </div>
      <StatusTimeline status={o.status} t={t} />
      {o.status === "delivered" && <ReviewForm order={o} myReviews={myReviews} lang={lang} ta={ta} onDone={reload} />}
    </div>
  );
};


export const OrdersList = ({ orders, t, lang, myReviews, reload }) => {
  const ta = t.account;
  if (orders === null) return <p className="text-sm text-stone-500 font-mono">…</p>;
  if (orders.length === 0) return (
    <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center" data-testid="account-no-orders">
      <Package size={32} className="mx-auto text-stone-300" /><p className="mt-4 font-serif text-xl text-stone-800 italic">{ta.noOrders}</p><p className="mt-1 text-xs text-stone-500">{ta.noOrdersHint}</p>
    </div>
  );
  return <div className="space-y-5">{orders.map((o) => <OrderCard key={o.id} o={o} t={t} lang={lang} myReviews={myReviews} reload={reload} />)}</div>;
};
